import {
  Badge,
  BadgeColor,
  BadgeContainer,
} from '@delmonicos/shared-design-system-react/src/components/badge/badge';
import { ButtonIcon } from '@delmonicos/shared-design-system-react/src/components/button-icon/button-icon';
import {
  Dialog,
  DialogPosition,
} from '@delmonicos/shared-design-system-react/src/components/dialog/dialog';
import {
  ResourceStatus,
  withProtectedResource,
} from '@delmonicos/shared-design-system-react/src/components/protected-resources/with-protected-resource.helper';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { GetRolesPaginatedModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/roles/models/inputs/get-roles-paginated.model';
import { safeHasAccessTo } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/helper/has-access-to';
import { transformPaginatedResponse } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/transform-api-paginated-response/transform-api-response';
import { ActionsByResource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/action.enum';
import { Resource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/resource.enum';
import {
  OnChangeFn,
  SortingState,
  functionalUpdate,
} from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { permissions } from '@app/core/authentication/authentication.slice';
import { getState } from '@app/core/helper/get-state';
import { useAppSelector } from '@app/core/hooks';
import { useCpmsBoGetRolesPaginatedQuery } from '@service/cpms-bo-role.service';
import {
  createColumnOrderChangeHandler,
  createHideColumnHandler,
} from '@shared/list-options/column-options.helper';

import { RoleListFilterSearchController } from '../role-list-filter-search/role-list-filter-search.controller';
import { RoleListFilterStateController } from '../role-list-filter-state/role-list-filter-state.controller';
import { RoleListFilterController } from '../role-list-filter/role-list-filter.controller';
import { RoleListOptionsController } from '../role-list-options/role-list-options.controller';
import { RoleListContextProvider, useRoleListContext } from '../role.context';
import {
  ROLE_LIST_FILTER_NAME_LIST,
  ROLE_LIST_SORT_FIELD,
} from './role-list.config';
import * as css from './role-list.module.scss';
import { RoleListView } from './role-list.view';

export const RoleListController = (): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const {
    getPaginatedQueryParameters,
    setGetPaginatedQueryParameters,
    getPaginatedLimitList,
    columnOptions,
    setColumnOptions,
  } = useRoleListContext();

  // USER PERMISSIONS //////////////////////////////////////////////////////////

  const userPermissions = useAppSelector(permissions);

  // ACTIONS ///////////////////////////////////////////////////////////////////

  const [actionFilterIsDialogOpen, setActionFilterIsDialogOpen] =
    useState(false);

  // DATA FETCHING /////////////////////////////////////////////////////////////

  const queryState = useCpmsBoGetRolesPaginatedQuery(
    getPaginatedQueryParameters,
  );

  const paginationState = useMemo(
    () =>
      getState({
        isLoading: queryState.isLoading,
        isError: queryState.isError,
        isFetching: queryState.isFetching,
      }),
    [queryState.isLoading, queryState.isError, queryState.isFetching],
  );

  // TODO: Should be moved in filter-search component
  //   if (debouncedTextToSearch && debouncedTextToSearch.trim().length > 0) {
  //     params.textToSearch = debouncedTextToSearch.trim() || '';
  //   }

  // TODO: Should be move in dedicated component
  const filterNameActiveList = ROLE_LIST_FILTER_NAME_LIST.filter(
    (filterName) => getPaginatedQueryParameters[filterName] !== undefined,
  );

  const filerActiveHiddenCount = Math.max(
    0,
    filterNameActiveList.reduce((count, filterName) => {
      const filterValue = getPaginatedQueryParameters[filterName] as any;

      if (Array.isArray(filterValue)) {
        return count + filterValue.length;
      }

      return count + 1;
    }, 0) - 3,
  );

  // PAGE TITLE ////////////////////////////////////////////////////////////////

  // Nothing to do

  // PAGE ACTIONS //////////////////////////////////////////////////////////////

  // Nothing to do

  // PAGE BREADCRUMB ///////////////////////////////////////////////////////////

  // Nothing to do

  // HANDLERS //////////////////////////////////////////////////////////////////

  const handlePaginationPageChange = (paginationPageNext: number) => {
    setGetPaginatedQueryParameters((current) => ({
      ...current,
      offset: (paginationPageNext - 1) * current.limit,
    }));
  };

  const handlePaginationLimitChange = (paginationLimitNext: number) => {
    setGetPaginatedQueryParameters((current) => ({
      ...current,
      limit: paginationLimitNext,
      offset: 0,
    }));
  };

  const handlePaginationSortingChange: OnChangeFn<SortingState> = (
    sortingState,
  ) => {
    setGetPaginatedQueryParameters((current) => {
      const paginationSorting0 = functionalUpdate(sortingState, [
        {
          desc: current.sortOrder === -1,
          id: current.sortField ?? ROLE_LIST_SORT_FIELD,
        },
      ]).at(0);

      return {
        ...current,
        sortField:
          paginationSorting0?.id as GetRolesPaginatedModel['sortField'],
        sortOrder: paginationSorting0?.desc ? -1 : 1,
      };
    });
  };

  const handleHideColumn = createHideColumnHandler(setColumnOptions);
  const handleColumnOrderChange =
    createColumnOrderChangeHandler(setColumnOptions);

  // RENDER ////////////////////////////////////////////////////////////////////

  const { Component: RoleListViewProtected, status: resourceStatus } =
    withProtectedResource(RoleListView, queryState, {
      isAuthorized: () =>
        safeHasAccessTo(userPermissions, {
          resourceName: Resource.ROLES,
          action: ActionsByResource.ROLES.READ,
        }),
      transformDataToResource: transformPaginatedResponse,
    })({
      paginationLimit: getPaginatedQueryParameters.limit,
      paginationLimitList: getPaginatedLimitList,
      paginationState,
      paginationSorting: [
        {
          desc: getPaginatedQueryParameters.sortOrder === -1,
          id: getPaginatedQueryParameters.sortField ?? ROLE_LIST_SORT_FIELD,
        },
      ],
      columnVisibility: columnOptions.columnVisibility,
      columnOrder: columnOptions.columnOrder,
      renderColumnSettings: () => <RoleListOptionsController />,
      onPaginationLimitChange: handlePaginationLimitChange,
      onPaginationPageChange: handlePaginationPageChange,
      onPaginationSortingChange: handlePaginationSortingChange,
      onHideColumn: handleHideColumn,
      onColumnOrderChange: handleColumnOrderChange,
    });

  return (
    <Stack flex={1} className={css.listContainer} direction="column">
      {resourceStatus === ResourceStatus.SUCCESS && (
        <Stack direction="row" justifyContent="space-between" gap={GapEnum.XL}>
          <Stack flex={1}>
            <RoleListFilterSearchController
              onCancel={() => {}}
              onSuccess={() => {}}
            />
          </Stack>
          <RoleListFilterStateController />
          <Stack justifyContent="end" gap={GapEnum.M}>
            <BadgeContainer
              badge={
                filerActiveHiddenCount > 0 && (
                  <Badge color={BadgeColor.Secondary}>
                    {`+${filerActiveHiddenCount}`}
                  </Badge>
                )
              }>
              <ButtonIcon
                icon="filter-funnel-01"
                onClick={() => setActionFilterIsDialogOpen(true)}
              />
            </BadgeContainer>
          </Stack>
        </Stack>
      )}
      <Stack className={css.tableContainer}>{RoleListViewProtected}</Stack>
      {resourceStatus === ResourceStatus.SUCCESS && (
        <Dialog
          open={actionFilterIsDialogOpen}
          onClose={() => setActionFilterIsDialogOpen(false)}
          position={DialogPosition.RIGHT}>
          <Dialog.Header
            icon="filter-funnel-01"
            onClose={() => setActionFilterIsDialogOpen(false)}
            title={t('$.role-list-filter.title')}
          />
          <Dialog.Content>
            <RoleListFilterController
              onCancel={() => setActionFilterIsDialogOpen(false)}
              onSuccess={() => setActionFilterIsDialogOpen(false)}
            />
          </Dialog.Content>
        </Dialog>
      )}
    </Stack>
  );
};

export const RoleListControllerWithContext = (): React.JSX.Element => {
  return (
    <RoleListContextProvider>
      <RoleListController />
    </RoleListContextProvider>
  );
};
